import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { getArticleSchema } from '../../utils/seoSchemas';
import { EDITORIAL_AUTHOR, INSIGHTS_MODIFIED, INSIGHTS_PUBLISHED } from './constants';

const TariffsVsLegislation = () => {
  const path = '/insights/tariffs-vs-legislation';
  const title = 'Tariffs vs. Legislation Modules on TradeRadar | User Guide';
  const description =
    'How TradeRadar’s tariffs and trade-events views relate to the legislation tracker, when AI extraction helps or misleads, and how to validate fields against primary legal texts.';

  const jsonLd = getArticleSchema({
    path,
    seo_title: title,
    seo_description: description,
    datePublished: INSIGHTS_PUBLISHED,
    dateModified: INSIGHTS_MODIFIED,
    authorPerson: EDITORIAL_AUTHOR,
  });

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEOHead
        title={title}
        description={description}
        path={path}
        type="article"
        author={EDITORIAL_AUTHOR.name}
        publishedTime={`${INSIGHTS_PUBLISHED}T12:00:00.000Z`}
        modifiedTime={`${INSIGHTS_MODIFIED}T12:00:00.000Z`}
        keywords="trade tariffs data, trade legislation tracker, AI trade extraction, verify trade policy"
        jsonLd={jsonLd}
      />

      <header className="mb-8">
        <p className="text-xs text-slate-500 dark:text-on-surface-variant mb-2">
          Insights · User guide · Published {INSIGHTS_PUBLISHED} · Updated {INSIGHTS_MODIFIED}
        </p>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-3">
          Tariffs vs. legislation on TradeRadar: what each module shows
        </h1>
        <p className="text-sm text-slate-600 dark:text-on-surface-variant">
          By {EDITORIAL_AUTHOR.name} ({EDITORIAL_AUTHOR.jobTitle})
        </p>
      </header>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Two lenses on policy friction</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          The <strong>tariffs</strong> and aggregate <strong>trade events</strong> experiences emphasise
          discrete economic measures: rate changes, quotas, sanctions lists, and import or export bans
          detected in news flow. They are optimised for time-series views, cross-country comparison, and the
          Global Trade Index inputs.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          The <strong>legislation</strong> module emphasises statutory and regulatory instruments: bills,
          executive orders, implementing regulations, and major amendments. It is optimised for tracing legal
          authority and text evolution, not for tick-by-tick market headlines.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">When stories overlap</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          A single policy shift often produces <em>both</em> a legislation record and a tariff event. For
          example, a law may authorise a new duty schedule; the law card captures the instrument, while the
          tariff pipeline captures the operational rate once publishers report it. Seeing only one side can
          mislead: the law may pass while implementation lags, or headlines may speculate before final text
          exists.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          Best practice is to open the country profile, read the legislation card for statutory context, then
          scan AI-extracted events for operational detail—always following outbound links to primary sources.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">AI extraction: strengths and pitfalls</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          Automated extraction scales coverage but can garble HS codes, confuse effective dates, or merge
          distinct announcements. TradeRadar surfaces confidence only indirectly (through editorial review
          samples and user corrections). High-stakes decisions—customs filing, transfer pricing, or
          sanctions screening—require human verification against official gazettes and legal counsel.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          If you spot a systematic error, please write to{' '}
          <a href="mailto:contact@traderadarglobal.com" className="text-primary-600 dark:text-primary hover:underline">
            contact@traderadarglobal.com
          </a>{' '}
          with the source URL; see{' '}
          <Link to="/editorial-standards" className="text-primary-600 dark:text-primary hover:underline">
            editorial standards
          </Link>{' '}
          for turnaround expectations.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Practical reading order</h2>
        <ol className="text-sm text-slate-700 dark:text-on-surface-variant space-y-2 list-decimal list-inside">
          <li>Open the country or sector view relevant to your question.</li>
          <li>Scan legislation for enabling authority and dates of effect.</li>
          <li>Review AI trade events for operational metrics (rates, scopes, parties).</li>
          <li>Cross-check both against the same primary document when possible.</li>
          <li>Use Compare GTI only after understanding per-country event lists.</li>
        </ol>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Related</h2>
        <ul className="text-sm text-slate-700 dark:text-on-surface-variant space-y-2">
          <li>
            <Link to="/tariffs" className="text-primary-600 dark:text-primary hover:underline">
              Tariffs explorer
            </Link>
          </li>
          <li>
            <Link to="/legislation" className="text-primary-600 dark:text-primary hover:underline">
              Legislation tracker
            </Link>
          </li>
          <li>
            <Link to="/insights/methodology" className="text-primary-600 dark:text-primary hover:underline">
              GTI methodology
            </Link>
          </li>
        </ul>
      </section>
    </article>
  );
};

export default TariffsVsLegislation;
